"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";

const tw = { tealBg: "bg-[#00778B]" };
const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

export default function News() {
  const [newsList, setNewsList] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [error, setError] = useState(null);

  // carousel
  const trackRef = useRef(null);
  const [perView, setPerView] = useState(3);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setMounted(true);
    fetchNews();
  }, []);

  useEffect(() => {
    // responsive perView: mobile 1, tablet 2, desktop 3
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) return 1; // <sm
      if (w < 1024) return 2; // <lg
      return 3; // lg+
    };
    const apply = () => setPerView(compute());
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  async function fetchNews() {
    try {
      const res = await fetch(`${API_BASE}/news/public`);
      if (!res.ok) throw new Error("Failed to fetch news");
      const data = await res.json();
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => {
            const da = new Date(a.date || a.created_at || 0).getTime();
            const db = new Date(b.date || b.created_at || 0).getTime();
            return db - da;
          })
        : [];

      setNewsList(sorted);
    } catch (e) {
      console.error(e);
      setError(e.message);
    }
  }

  // max index for sliding by 1 (so 4 items with perView=3 => maxIndex=1)
  const maxIndex = useMemo(
    () => Math.max(0, newsList.length - perView),
    [newsList.length, perView]
  );

  // clamp index when perView changes
  useEffect(() => {
    setIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const positions = useMemo(
    () => (newsList.length === 0 ? 0 : maxIndex + 1),
    [maxIndex, newsList.length]
  );

  const scrollToIndex = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(i, maxIndex));
    const card = el.querySelector(`[data-card-index="${clamped}"]`);
    if (card) {
      el.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
      setIndex(clamped);
    }
  };

  const prev = () => scrollToIndex(index - 1);
  const next = () => scrollToIndex(index + 1);

  if (!mounted) return null;

  if (error) {
    return (
      <section className="py-16 sm:py-24 bg-[#f7fafc] text-center">
        <p className="text-red-500">Error loading news: {error}</p>
      </section>
    );
  }

  return (
    <section id="news" className="py-16 sm:py-24 bg-[#f7fafc]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#003B5C]">
            News
          </h2>

          <Link
            href="/news"
            className={`inline-block px-6 py-3 rounded-full text-sm font-medium ${tw.tealBg} text-white hover:opacity-90 text-center`}
          >
            More News
          </Link>
        </div>

        {newsList.length === 0 ? (
          <p className="text-center text-gray-500">No news available.</p>
        ) : (
          <div className="relative">
            {/* Arrows (muncul kalau item lebih banyak dari perView) */}
            {newsList.length > perView && (
              <>
                <button
                  onClick={prev}
                  disabled={index === 0}
                  className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white shadow border border-zinc-200 grid place-items-center disabled:opacity-40"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5 text-[#003B5C]" />
                </button>
                <button
                  onClick={next}
                  disabled={index === maxIndex}
                  className="absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white shadow border border-zinc-200 grid place-items-center disabled:opacity-40"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5 text-[#003B5C]" />
                </button>
              </>
            )}

            {/* Track */}
            <div
              ref={trackRef}
              className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2"
              style={{ scrollbarWidth: "none" }}
            >
              {newsList.map((item, i) => (
                <article
                  key={item.id}
                  data-card-index={i}
                  className={[
                    "snap-start shrink-0 bg-white rounded-xl shadow-md p-6 border border-gray-100",
                    "w-[85%] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]",
                  ].join(" ")}
                >
                  {item.images?.length > 0 ? (
                    <img
                      src={item.images[0].url}
                      alt={item.title_en}
                      className="w-full h-40 object-cover rounded-md mb-3"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-40 bg-zinc-200 rounded-md mb-3 grid place-items-center text-zinc-500 text-sm">
                      No Image
                    </div>
                  )}

                  <h3 className="text-lg font-bold text-[#003B5C] mb-1">
                    <Link href={`/news/${item.id}`} className="hover:underline">
                      {item.title_en}
                    </Link>
                  </h3>

                  <p className="text-sm text-gray-600 mb-2 line-clamp-3">
                    {item.excerpt_en}
                  </p>

                  {item.date && (
                    <span className="text-xs text-gray-400">{item.date}</span>
                  )}

                  <Link
                    href={`/news/${item.id}`}
                    className="mt-2 block text-xs text-[#00778B] font-semibold hover:underline"
                  >
                    Read More
                  </Link>
                </article>
              ))}
            </div>

            {/* Dots */}
            {positions > 1 && (
              <div className="mt-6 flex justify-center gap-2">
                {Array.from({ length: positions }).map((_, p) => (
                  <button
                    key={p}
                    onClick={() => scrollToIndex(p)}
                    className={[
                      "h-2.5 rounded-full transition-all",
                      p === index ? "w-6 bg-[#003B5C]" : "w-2.5 bg-[#003B5C]/30",
                    ].join(" ")}
                    aria-label={`Go to slide ${p + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}